import React, { useRef, useCallback } from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { useAtlas } from '../../context/AtlasContext';
import { LocationBlock } from './LocationBlock';
import { MappingBlock } from './MappingBlock';
import { SkeletonBlock } from './SkeletonBlock';
import { AtlasMapService } from '../../lib/atlasMapService';

const BLOCK_SIZE = 200;

interface AtlasCanvasProps {
    readOnly?: boolean;
}

export const AtlasCanvas: React.FC<AtlasCanvasProps> = ({ readOnly = false }) => {
    const {
        tool,
        placedLocations,
        mapBlocks,
        viewportState,
        setViewportState,
        selectedLocationId,
        setSelectedLocationId,
        selectedMapBlockId,
        setSelectedMapBlockId,
        setIsCreateModalOpen,
        setPendingPlacement,
        generateMapBlock,
        isGenerating,
        unplaceLocation,
        deleteMapBlock
    } = useAtlas();

    const transformRef = useRef<ReactZoomPanPinchRef>(null);
    const gridContainerRef = useRef<HTMLDivElement>(null);
    const scaleRef = useRef(1);

    // Track pending skeleton block position
    const [skeletonPosition, setSkeletonPosition] = React.useState<{ x: number; y: number } | null>(null);

    // Handle canvas click based on current tool
    const handleCanvasClick = useCallback((e: React.MouseEvent) => {
        // Don't allow editing in read-only mode
        if (readOnly) return;
        // Only handle if clicking on empty canvas, not on blocks
        if ((e.target as HTMLElement).closest('[data-block]')) return;

        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const scale = scaleRef.current;
        const state = viewportState;

        // Calculate position in canvas coordinates
        const x = (e.clientX - rect.left - state.positionX) / scale;
        const y = (e.clientY - rect.top - state.positionY) / scale;

        // Snap to grid
        const snappedX = Math.floor(x / BLOCK_SIZE) * BLOCK_SIZE;
        const snappedY = Math.floor(y / BLOCK_SIZE) * BLOCK_SIZE;

        if (tool === 'pin') {
            // Open location create modal at clicked position
            setPendingPlacement({ x: snappedX, y: snappedY });
            setIsCreateModalOpen(true);
        } else if (tool === 'block') {
            // Check if position is adjacent to existing content
            const isValid = AtlasMapService.isValidBlockPosition(
                snappedX, snappedY,
                placedLocations,
                mapBlocks,
                BLOCK_SIZE
            );

            if (isValid) {
                // Show skeleton and start generation
                setSkeletonPosition({ x: snappedX, y: snappedY });
                handleGenerateBlock(snappedX, snappedY);
            }
        } else if (tool === 'select') {
            // Deselect everything
            setSelectedLocationId(null);
            setSelectedMapBlockId(null);
        }
    }, [tool, viewportState, placedLocations, mapBlocks, setPendingPlacement, setIsCreateModalOpen, setSelectedLocationId, setSelectedMapBlockId]);

    // Generate a new map block
    const handleGenerateBlock = async (x: number, y: number) => {
        const adjacentBlocks = AtlasMapService.findAdjacentBlocks(
            x, y,
            placedLocations,
            mapBlocks,
            BLOCK_SIZE
        );

        const block = await generateMapBlock(x, y, adjacentBlocks);

        // Clear skeleton after generation
        setSkeletonPosition(null);

        if (block) {
            setSelectedMapBlockId(block.id);
        }
    };

    // Get hover preview for block tool
    const [hoverPosition, setHoverPosition] = React.useState<{ x: number; y: number; valid: boolean } | null>(null);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (tool !== 'block') {
            setHoverPosition(null);
            return;
        }

        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const scale = scaleRef.current;
        const state = viewportState;

        const x = (e.clientX - rect.left - state.positionX) / scale;
        const y = (e.clientY - rect.top - state.positionY) / scale;

        const snappedX = Math.floor(x / BLOCK_SIZE) * BLOCK_SIZE;
        const snappedY = Math.floor(y / BLOCK_SIZE) * BLOCK_SIZE;

        // Check if position is already occupied
        const isOccupied = [...placedLocations, ...mapBlocks].some(block => {
            const bx = 'canvas_x' in block ? block.canvas_x : block.canvas_x;
            const by = 'canvas_y' in block ? block.canvas_y : block.canvas_y;
            return bx === snappedX && by === snappedY;
        });

        if (isOccupied) {
            setHoverPosition(null);
            return;
        }

        const isValid = AtlasMapService.isValidBlockPosition(
            snappedX, snappedY,
            placedLocations,
            mapBlocks,
            BLOCK_SIZE
        );

        setHoverPosition({ x: snappedX, y: snappedY, valid: isValid });
    }, [tool, viewportState, placedLocations, mapBlocks]);

    const handleMouseLeave = () => {
        setHoverPosition(null);
    };

    return (
        <div
            ref={gridContainerRef}
            className="w-full h-full bg-zinc-950"
            style={{
                backgroundImage: `
                    linear-gradient(to right, #1a1a1a 1px, transparent 1px),
                    linear-gradient(to bottom, #1a1a1a 1px, transparent 1px)
                `,
                backgroundSize: '24px 24px',
                backgroundPosition: '0px 0px'
            }}
        >
            <TransformWrapper
                ref={transformRef}
                initialScale={1}
                minScale={0.1}
                maxScale={5}
                limitToBounds={false}
                wheel={{ step: 0.1 }}
                panning={{
                    // Allow panning when:
                    // 1. Pan tool is selected (any mouse button)
                    // 2. Middle mouse button is pressed (any tool)
                    disabled: tool !== 'pan',
                    velocityDisabled: true,
                    allowMiddleClickPan: true // Enable middle-click panning regardless of tool
                }}
                doubleClick={{ disabled: true }}
                onInit={() => {
                    // Restore viewport state
                    if (viewportState.scale !== 1 || viewportState.positionX !== 0 || viewportState.positionY !== 0) {
                        setTimeout(() => {
                            transformRef.current?.setTransform(
                                viewportState.positionX,
                                viewportState.positionY,
                                viewportState.scale,
                                0
                            );
                        }, 50);
                    }
                }}
                onTransformed={(r) => {
                    const { positionX, positionY, scale } = r.instance.transformState;
                    scaleRef.current = scale;

                    // Update grid background
                    if (gridContainerRef.current) {
                        gridContainerRef.current.style.backgroundSize = `${24 * scale}px ${24 * scale}px`;
                        gridContainerRef.current.style.backgroundPosition = `${positionX}px ${positionY}px`;
                    }

                    // Save viewport state
                    setViewportState({ positionX, positionY, scale });
                }}
            >
                {({ zoomIn, zoomOut, resetTransform, setTransform }) => (
                    <TransformComponent
                        wrapperClass={`w-full h-full ${tool === 'pan' ? 'cursor-grab active:cursor-grabbing' : ''}`}
                        contentClass="w-full h-full"
                        wrapperStyle={{ width: '100%', height: '100%' }}
                    >
                        <div
                            className="relative min-w-[10000px] min-h-[10000px]"
                            onClick={handleCanvasClick}
                            onMouseMove={handleMouseMove}
                            onMouseLeave={handleMouseLeave}
                            style={{
                                transform: 'translate(5000px, 5000px)' // Center the origin
                            }}
                        >
                            {/* Map Blocks (rendered UNDER locations) */}
                            {mapBlocks.map((block) => (
                                <MappingBlock
                                    key={block.id}
                                    block={block}
                                    isSelected={selectedMapBlockId === block.id}
                                    onSelect={() => {
                                        if (tool === 'eraser') {
                                            deleteMapBlock(block.id);
                                        } else {
                                            setSelectedMapBlockId(block.id);
                                        }
                                    }}
                                    scale={scaleRef.current}
                                    tool={tool}
                                />
                            ))}

                            {/* Location Blocks (rendered ON TOP) */}
                            {placedLocations.map((location) => (
                                <LocationBlock
                                    key={location.id}
                                    location={location}
                                    isSelected={selectedLocationId === location.id}
                                    onSelect={() => {
                                        if (tool === 'eraser') {
                                            unplaceLocation(location.id);
                                        } else {
                                            setSelectedLocationId(location.id);
                                        }
                                    }}
                                    scale={scaleRef.current}
                                    tool={tool}
                                />
                            ))}

                            {/* Skeleton Block (during generation) */}
                            {skeletonPosition && isGenerating && (
                                <SkeletonBlock
                                    x={skeletonPosition.x}
                                    y={skeletonPosition.y}
                                    size={BLOCK_SIZE}
                                    isGenerating={true}
                                />
                            )}

                            {/* Hover Preview for Block Tool */}
                            {hoverPosition && !skeletonPosition && (
                                <div
                                    className={`absolute pointer-events-none border-2 border-dashed rounded-lg transition-colors ${
                                        hoverPosition.valid
                                            ? 'border-indigo-500/50 bg-indigo-500/10'
                                            : 'border-red-500/50 bg-red-500/10'
                                    }`}
                                    style={{
                                        left: hoverPosition.x,
                                        top: hoverPosition.y,
                                        width: BLOCK_SIZE,
                                        height: BLOCK_SIZE
                                    }}
                                />
                            )}
                        </div>
                    </TransformComponent>
                )}
            </TransformWrapper>
        </div>
    );
};
